import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Star, Sparkles, PartyPopper } from "lucide-react";

// Milestone thresholds
const MILESTONES = [50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];

// Get milestone info based on user count
export const getMilestoneInfo = (count) => {
  // Find the most recent milestone reached
  const reachedMilestones = MILESTONES.filter(m => count >= m);
  if (reachedMilestones.length === 0) return null;
  
  const currentMilestone = reachedMilestones[reachedMilestones.length - 1];
  const nextMilestone = MILESTONES.find(m => m > count);
  
  return {
    current: currentMilestone,
    next: nextMilestone,
    progress: nextMilestone ? ((count - currentMilestone) / (nextMilestone - currentMilestone)) * 100 : 100
  };
};

// Check if we should celebrate (within 5 users of a milestone)
export const shouldCelebrate = (count) => {
  return MILESTONES.some(milestone => count >= milestone && count <= milestone + 5);
};

// Confetti particle component
const ConfettiParticle = ({ delay, color, startX }) => {
  const colors = {
    cyan: "bg-neon-cyan",
    purple: "bg-neon-purple", 
    pink: "bg-pink-500",
    yellow: "bg-yellow-400",
    green: "bg-green-400",
  };

  return (
    <motion.div
      className={`absolute w-3 h-3 rounded-sm ${colors[color]} shadow-lg`}
      style={{ left: `${startX}%` }}
      initial={{ 
        y: -20, 
        x: 0,
        rotate: 0,
        opacity: 1,
        scale: 1
      }}
      animate={{ 
        y: [0, 400, 600],
        x: [0, (Math.random() - 0.5) * 200, (Math.random() - 0.5) * 300],
        rotate: [0, 360, 720],
        opacity: [1, 1, 0],
        scale: [1, 0.8, 0.5]
      }}
      transition={{ 
        duration: 3,
        delay: delay,
        ease: "easeOut"
      }}
    />
  );
};

// Sparkle burst component
const SparkleBurst = ({ delay }) => {
  return (
    <motion.div
      className="absolute"
      style={{ 
        left: `${20 + Math.random() * 60}%`, 
        top: `${20 + Math.random() * 40}%` 
      }}
      initial={{ scale: 0, opacity: 0 }}
      animate={{ 
        scale: [0, 1.5, 0],
        opacity: [0, 1, 0],
        rotate: [0, 180]
      }}
      transition={{ duration: 0.8, delay }}
    >
      <Sparkles className="w-8 h-8 text-yellow-400" />
    </motion.div>
  );
};

// Main celebration modal
export const MilestoneCelebration = ({ milestone, onClose }) => {
  const [particles, setParticles] = useState([]);
  const [sparkles, setSparkles] = useState([]);
  
  useEffect(() => {
    // Generate confetti particles
    const colors = ["cyan", "purple", "pink", "yellow", "green"];
    const newParticles = [];
    for (let i = 0; i < 50; i++) {
      newParticles.push({
        id: i,
        color: colors[Math.floor(Math.random() * colors.length)],
        delay: Math.random() * 0.5,
        startX: Math.random() * 100
      });
    }
    setParticles(newParticles);
    
    // Generate sparkles
    const newSparkles = [];
    for (let i = 0; i < 8; i++) {
      newSparkles.push({
        id: i,
        delay: i * 0.15
      });
    }
    setSparkles(newSparkles);
    
    // Auto close after 4 seconds
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    
    return () => clearTimeout(timer);
  }, [onClose]);

  const getMilestoneEmoji = (count) => {
    if (count >= 100000) return "🏆";
    if (count >= 50000) return "👑";
    if (count >= 10000) return "🌟";
    if (count >= 5000) return "💎";
    if (count >= 1000) return "🔥";
    if (count >= 500) return "⭐";
    if (count >= 100) return "🎉";
    return "🎊";
  };

  const getMilestoneMessage = (count) => {
    if (count >= 100000) return "¡Legendario!";
    if (count >= 50000) return "¡Increíble comunidad!";
    if (count >= 10000) return "¡10K Punks!";
    if (count >= 5000) return "¡5K Punks!";
    if (count >= 1000) return "¡Mil Punks!";
    if (count >= 500) return "¡500 Punks!";
    if (count >= 100) return "¡100 Punks!";
    if (count >= 50) return "¡50 Punks!";
    return "¡Hito alcanzado!";
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Confetti particles */}
      <div className="absolute inset-0 overflow-hidden">
        {particles.map(p => (
          <ConfettiParticle key={p.id} {...p} />
        ))}
      </div>
      
      {/* Sparkle bursts */}
      <div className="absolute inset-0 overflow-hidden">
        {sparkles.map(s => (
          <SparkleBurst key={s.id} delay={s.delay} />
        ))}
      </div>
      
      {/* Celebration badge */}
      <motion.div
        className="relative z-10 pointer-events-auto"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        exit={{ scale: 0, rotate: 180 }}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
        onClick={onClose}
      >
        <div className="relative">
          {/* Glow effect */}
          <motion.div 
            className="absolute inset-0 bg-neon-cyan/30 rounded-3xl blur-3xl"
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.8, 0.5]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          
          {/* Main card */}
          <div className="relative bg-void-paper/95 backdrop-blur-xl border-2 border-neon-cyan/50 rounded-3xl p-8 text-center shadow-2xl shadow-neon-cyan/20">
            {/* Trophy icon with pulse */}
            <motion.div 
              className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-neon-cyan/20 to-neon-purple/20 border-2 border-neon-cyan/50 flex items-center justify-center"
              animate={{ 
                boxShadow: [
                  "0 0 20px rgba(0,243,255,0.3)",
                  "0 0 40px rgba(0,243,255,0.6)",
                  "0 0 20px rgba(0,243,255,0.3)"
                ]
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <span className="text-5xl">{getMilestoneEmoji(milestone)}</span>
            </motion.div>
            
            {/* Celebration text */}
            <motion.h2 
              className="font-orbitron text-3xl text-white mb-2"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {getMilestoneMessage(milestone)}
            </motion.h2>
            
            {/* Milestone number */}
            <motion.div 
              className="flex items-center justify-center gap-2 mb-4"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Trophy className="w-6 h-6 text-yellow-400" />
              <span className="font-orbitron text-4xl font-bold bg-gradient-to-r from-neon-cyan to-neon-purple bg-clip-text text-transparent">
                {milestone.toLocaleString()}
              </span>
              <Trophy className="w-6 h-6 text-yellow-400" />
            </motion.div>
            
            <motion.p 
              className="text-slate-400 font-rajdhani text-lg"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              usuarios en la comunidad MoodPunk
            </motion.p>
            
            {/* Tap to close hint */}
            <motion.p 
              className="mt-4 text-xs text-slate-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              Toca para continuar
            </motion.p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Hook to manage milestone celebration state
export const useMilestoneCelebration = (userCount) => {
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebratedMilestone, setCelebratedMilestone] = useState(null);
  
  useEffect(() => {
    if (userCount === 0) return;
    
    // Check localStorage for last celebrated milestone
    const lastCelebrated = localStorage.getItem("moodpunk_last_milestone");
    const lastCelebratedNum = lastCelebrated ? parseInt(lastCelebrated, 10) : 0;
    
    // Find the current milestone
    const currentMilestone = MILESTONES.filter(m => userCount >= m).pop();
    
    // If we've reached a new milestone that hasn't been celebrated
    if (currentMilestone && currentMilestone > lastCelebratedNum) {
      setCelebratedMilestone(currentMilestone);
      setShowCelebration(true);
      localStorage.setItem("moodpunk_last_milestone", currentMilestone.toString());
    }
  }, [userCount]);
  
  const closeCelebration = useCallback(() => {
    setShowCelebration(false);
  }, []);
  
  return {
    showCelebration,
    celebratedMilestone,
    closeCelebration
  };
};

export default MilestoneCelebration;
